import { EmbedsHomeTemplate } from '@fontbold/ui';
import { createFileRoute } from '@tanstack/react-router';
import { AppTabBar, AppTopBar } from '#/components/AppChrome';
import { AppNewsletter, openNewsletter } from '#/components/AppNewsletter';
import { paginateLatestReels } from '#/lib/latest-reels';
import { cityCountsFrom, createLocationLabels } from '#/lib/location';
import { locationSummaryQueries } from '#/lib/location-summary';
import { embedMeta } from '#/lib/placeholder-data';
import { useRecent } from '#/lib/recent';
import { relativeAgo } from '#/lib/relative-time';
import { embedPlatform, siteTitle, socialLinks } from '#/lib/site';
import { getSpace } from '#/lib/space-fn';
import { spaceLogo } from '#/lib/space-logo';
import { EMBEDS_HOME_TRANSLATIONS } from '#/lib/translations';
import {
  type AllPlace,
  allPlacesQueries,
  featuredEmbedQueries,
} from './-queries';

function toEmbedCard(place: AllPlace) {
  const meta = embedMeta(place.id);

  return {
    id: place.id,
    name: place.name,
    href: place.href,
    area: [place.districtLabel, place.cityLabel].filter(Boolean).join(', '),
    cover: place.poster,
    views: meta.views,
    ago: relativeAgo(place.latestReel?.publishedAt ?? null) ?? meta.ago,
  };
}

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>) => {
    const page = Number(search.page);

    return {
      page: Number.isInteger(page) ? Math.max(1, page) : 1,
    };
  },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ context, deps }) => {
    const space = await getSpace();
    if (!space) {
      return {
        space: null,
        embeds: [],
        featured: null,
        cities: [],
        placeCount: 0,
        reelCount: 0,
        pagination: null,
      };
    }

    const cities = await context.queryClient.ensureQueryData(
      locationSummaryQueries.cities(space.id),
    );
    const result = await context.queryClient.ensureQueryData(
      allPlacesQueries.places(space.id, 100, createLocationLabels(cities)),
    );

    const reels = paginateLatestReels(result.places, deps.page);
    const embeds = reels.places.map(toEmbedCard);

    const counts = cityCountsFrom(result.places);

    const featuredPlace = reels.featuredPlace;
    const featuredEmbed = featuredPlace
      ? await context.queryClient.ensureQueryData(
          featuredEmbedQueries.embed(space.id, featuredPlace.slug),
        )
      : null;

    return {
      space,
      embeds,
      featured: featuredPlace ? toEmbedCard(featuredPlace) : null,
      featuredAgo: relativeAgo(featuredEmbed?.publishedAt ?? null),
      featuredSourceUrl: featuredEmbed?.url ?? null,
      featuredPlatform: embedPlatform(featuredEmbed?.format),
      placeCount: result.places.length,
      reelCount: reels.totalCount,
      pagination: {
        currentPage: reels.currentPage,
        totalPages: reels.totalPages,
        hasPreviousPage: reels.hasPreviousPage,
        hasNextPage: reels.hasNextPage,
      },
      cities: cities
        .map((city) => ({
          slug: city.slug,
          label: city.name,
          count: counts.get(city.slug) ?? 0,
          href: `/kaart?city=${city.slug}`,
        }))
        .filter((city) => city.count > 0)
        .sort((a, b) => b.count - a.count),
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: siteTitle(loaderData?.space) }],
  }),
  component: HomePage,
});

function HomePage() {
  const {
    space,
    embeds,
    featured,
    featuredAgo,
    featuredSourceUrl,
    featuredPlatform,
    cities,
    placeCount,
    reelCount,
    pagination,
  } = Route.useLoaderData();
  const recent = useRecent();

  if (!space) return null;

  const logo = spaceLogo(space);

  return (
    <EmbedsHomeTemplate
      topBar={
        <AppTopBar space={space} active="home" onNewsletter={openNewsletter} />
      }
      tabBar={<AppTabBar space={space} active="home" />}
      newsletter={<AppNewsletter spaceId={space.id} logo={logo} />}
      curator={{
        name: space.name,
        logo,
        host: space.curator ? `Host: ${space.curator.name}` : undefined,
        placeCount,
        cityCount: cities.length,
        aboutHref: '/over',
      }}
      translations={{
        ...EMBEDS_HOME_TRANSLATIONS,
        embedsMeta: `${reelCount} reels`,
      }}
      socialLinks={socialLinks(space)}
      cities={cities}
      recent={recent.map((place) => ({
        id: place.id,
        name: place.name,
        href: place.path,
        ago: place.ago,
        cover: place.cover,
      }))}
      feature={
        featured
          ? {
              ...featured,
              // Real publish date, but no real view count yet (CF-7) - drop
              // the placeholder views rather than pair a real date with a
              // fake number.
              views: undefined,
              ago: featuredAgo,
              sourceUrl: featuredSourceUrl,
              platform: featuredPlatform,
            }
          : undefined
      }
      embeds={embeds}
      pagination={
        pagination
          ? {
              ...pagination,
              pageHref: (page) => (page === 1 ? '/' : `/?page=${page}`),
              translations: {
                previousLabel: 'Vorige',
                nextLabel: 'Volgende',
                previousAriaLabel: 'Vorige pagina',
                nextAriaLabel: 'Volgende pagina',
                pageAriaLabel: (page) => `Pagina ${page}`,
                navAriaLabel: 'Paginering van laatste reels',
                counterLabel: (current, total) =>
                  `Pagina ${current} van ${total}`,
              },
            }
          : undefined
      }
      mapHref="/kaart"
      onNewsletter={openNewsletter}
    />
  );
}
